// K 线图组件（Web 简化版） - lightweight-charts + IndexedDB 缓存 + 后端 API
import { useEffect, useRef, useState } from 'react';
import { Spin, Empty, Tag, Space } from 'antd';
import { createChart, type IChartApi, type ISeriesApi, type Time } from 'lightweight-charts';
import type { Candle, StockBasic } from '../../shared/types';
import { getCachedKlines, setCachedKlines, mergeCandles, shouldRefresh } from '../lib/klineCache';
import { api } from '../lib/api';

interface Props {
  stock: StockBasic | null;
  height?: number;
}

export default function KLineChart({ stock, height = 320 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candleCount, setCandleCount] = useState(0);
  const [source, setSource] = useState<'cache' | 'fresh' | 'mixed'>('cache');

  // 初始化图表
useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: { background: { color: '#0d1117' }, textColor: '#c9d1d9' },
      grid: { vertLines: { color: '#21262d' }, horzLines: { color: '#21262d' } },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#ff4d4f',
      downColor: '#52c41a',
      borderUpColor: '#ff4d4f',
      borderDownColor: '#52c41a',
      wickUpColor: '#ff4d4f',
      wickDownColor: '#52c41a',
    });
    const volSeries = chart.addHistogramSeries({
      color: '#26a69a',
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    });
    volSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volSeriesRef.current = volSeries;

    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volSeriesRef.current = null;
    };
  }, [height]);

  // 加载数据
useEffect(() => {
    if (!stock) return;
    let cancelled = false;

    const load = async () => {
      if (!candleSeriesRef.current || !volSeriesRef.current) return;
      setLoading(true);
      setError(null);

      try {
        // 1) 读 IndexedDB 缓存
const cached = await getCachedKlines(stock.code);
        let candles: Candle[] = cached?.candles ?? [];
        let usedSource: 'cache' | 'fresh' | 'mixed' = 'cache';

        if (!cached || shouldRefresh(cached.lastFetchAt)) {
          // 2) 调后端 API
          const freshResp = await api.getKlineHistory({
            code: stock.code,
            market: stock.market,
            type: stock.type,
          });
          if (cancelled) return;
          const fresh = (freshResp.candles ?? []) as Candle[];
          if (fresh.length > 0) {
            if (candles.length > 0) {
              candles = mergeCandles(candles, fresh);
              usedSource = 'mixed';
            } else {
              candles = fresh;
              usedSource = 'fresh';
            }
            await setCachedKlines(stock.code, candles);
          }
        }

        if (cancelled) return;
        if (candles.length === 0) {
          setError('暂无历史数据');
          setLoading(false);
          return;
        }

        candleSeriesRef.current.setData(
          candles.map((c) => ({
            time: c.time as Time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          }))
        );
        volSeriesRef.current.setData(
          candles.map((c) => ({
            time: c.time as Time,
            value: c.volume ?? 0,
            color: c.close >= c.open ? '#ff4d4f33' : '#52c41a33',
          }))
        );

        chartRef.current?.timeScale().fitContent();
        setCandleCount(candles.length);
        setSource(usedSource);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [stock?.code, stock?.market, stock?.type]);

  if (!stock) return <Empty description="请选择股票" />;

  return (
    <div style={{ position: 'relative' }}>
      <Space style={{ marginBottom: 4, fontSize: 11 }}>
        <Tag color="default">
          {stock.name} · {stock.code}
        </Tag>
        <span style={{ color: '#8b949e' }}>共 {candleCount} 根 K 线</span>
        {source === 'cache' && <Tag color="success">本地缓存</Tag>}
        {source === 'fresh' && <Tag color="processing">实时拉取</Tag>}
        {source === 'mixed' && <Tag color="warning">增量合并</Tag>}
      </Space>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height,
          background: '#0d1117',
          border: '1px solid #30363d',
          borderRadius: 4,
        }}
      />
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 30,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(13, 17, 23, 0.6)',
          }}
        >
          <Spin tip="加载 K 线..." />
        </div>
      )}
      {!loading && error && (
        <div
          style={{
            position: 'absolute',
            top: 30,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Empty description={error} />
        </div>
      )}
    </div>
  );
}